import { useCallback, useEffect, useState } from "react";

import type { ApiClient } from "../api/client";
import type { Calendar, Entry, Me } from "../api/types";
import { CalendarGrid } from "../components/CalendarGrid";
import { DayPanel } from "../components/DayPanel";
import { MonthNav } from "../components/MonthNav";
import { toMonthString, type MonthRef } from "../lib/dates";
import { formatDuration } from "../lib/duration";

interface Props {
  api: ApiClient;
  me: Me;
  month: MonthRef;
  onMonthChange: (month: MonthRef) => void;
  onStepMonth: (delta: number) => void;
  targetUserId: string | null;
}

export function CalendarPage({
  api,
  me,
  month,
  onMonthChange,
  onStepMonth,
  targetUserId,
}: Props): JSX.Element {
  const [calendar, setCalendar] = useState<Calendar | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const monthString = toMonthString(month);
  const scope = targetUserId ?? undefined;

  const load = useCallback(async () => {
    setError(null);
    try {
      const [calendarData, entryData] = await Promise.all([
        api.calendar(monthString, scope),
        api.entries(monthString, scope),
      ]);
      setCalendar(calendarData);
      setEntries(entryData);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load the calendar.");
    }
  }, [api, monthString, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  // Selecting a day in one month and then paging to another would otherwise leave a stale
  // day open that no longer exists in the grid.
  useEffect(() => {
    setSelected(null);
  }, [monthString, scope]);

  const selectedDay = calendar?.days.find((day) => day.work_date === selected) ?? null;
  const selectedEntries = selected
    ? entries.filter((entry) => entry.work_date === selected)
    : [];

  return (
    <div className="page">
      <MonthNav month={month} onStep={onStepMonth} onChange={onMonthChange}>
        {calendar && (
          <span className="month-total">
            {formatDuration(calendar.minutes)} logged
            {targetUserId && ` · ${calendar.display_name}`}
          </span>
        )}
      </MonthNav>

      {error && <p className="error">{error}</p>}

      <div className="calendar-layout">
        {calendar ? (
          <CalendarGrid days={calendar.days} selected={selected} onSelect={setSelected} />
        ) : (
          <p className="muted">Loading…</p>
        )}

        {selectedDay && (
          <DayPanel
            api={api}
            me={me}
            day={selectedDay}
            entries={selectedEntries}
            targetUserId={targetUserId}
            onChanged={load}
          />
        )}
      </div>
    </div>
  );
}
